import { parseDbTimestamp } from "@repo/shared";
import type { ClientFile } from "@repo/shared/types";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, Download, Info, Loader2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { get } from "@/lib/api/_client";
import { useT } from "@/lib/i18n/use-t";
import { log } from "@/lib/logger/browser-logger";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { formatBytes, getFileTypeIcon } from "@/lib/utils";
import { T } from "./client-file-widget.i18n";

interface ClientFileWidgetProps {
  file: {
    id: string;
    clientId: string;
    fileName: string;
    fileType: "pdf" | "document" | "image" | "audio";
    size: number;
    createdAt: string;
    summary: string | null;
    processingStatus: "pending" | "processing" | "done" | "failed" | "skipped";
  };
}

export function ClientFileWidget({ file: initial }: ClientFileWidgetProps) {
  const { formatDate } = useRegionalFormat();
  const t = useT(T);
  const [showSummary, setShowSummary] = useState(false);
  const Icon = getFileTypeIcon(initial.fileType);

  // Lazy-load full detail (summary + extractedText) only when user opens the modal,
  // so the chat sidebar doesn't fetch every file's transcript up front.
  const {
    data: detail,
    isLoading: detailLoading,
    error: detailError,
    refetch,
  } = useQuery({
    queryKey: ["client-files", "single", initial.id],
    queryFn: () => get<ClientFile>(`/internal/client-files/single/${initial.id}`),
    enabled: showSummary,
    staleTime: 60_000,
  });

  const handleDownload = async () => {
    try {
      const { url } = await get<{ url: string }>(
        `/internal/client-files/${initial.clientId}/${initial.id}/url`,
      );
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      log.error({ err, fileId: initial.id }, "Failed to fetch file download URL");
    }
  };

  return (
    <>
      <div className="w-full min-w-0 max-w-full flex items-start gap-2 text-xs p-2 rounded bg-muted/50 overflow-hidden">
        <div className="h-6 w-6 rounded bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Icon className="h-3 w-3 text-primary" />
        </div>
        <div className="flex-1 min-w-0 overflow-hidden">
          <div className="font-medium truncate">{initial.fileName}</div>
          <div className="text-muted-foreground truncate">
            {formatBytes(initial.size)} &bull; {formatDate(parseDbTimestamp(initial.createdAt))}
          </div>
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={t.download}
                onClick={handleDownload}
                className="p-1 rounded hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                <Download className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent>{t.download}</TooltipContent>
          </Tooltip>
          {(initial.summary || initial.processingStatus === "done") && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => setShowSummary(true)}
                  aria-label={t.showSummary}
                  className="p-1 rounded hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent>{t.showSummary}</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      <Dialog open={showSummary} onOpenChange={setShowSummary}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{initial.fileName}</DialogTitle>
            <DialogDescription className="sr-only">{t.summaryAndText}</DialogDescription>
          </DialogHeader>
          {detailLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" /> {t.loading}
            </div>
          )}
          {detailError && !detailLoading && (
            <div className="flex flex-col items-center gap-3 text-sm py-8">
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-4 w-4" />
                {detailError instanceof Error ? detailError.message : t.loadFailed}
              </div>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                {t.retry}
              </Button>
            </div>
          )}
          {detail && (
            <div className="space-y-4 overflow-y-auto pr-1">
              {detail.summary && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
                    {t.summary}
                  </div>
                  <p className="text-sm whitespace-pre-line">{detail.summary}</p>
                </div>
              )}
              {detail.extractedText && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
                    {t.extractedText}
                  </div>
                  <pre className="text-xs whitespace-pre-wrap font-mono bg-muted/30 p-3 rounded-md max-h-[40vh] overflow-y-auto">
                    {detail.extractedText}
                  </pre>
                </div>
              )}
              {!detail.summary && !detail.extractedText && (
                <p className="text-sm text-muted-foreground">
                  {t.nothingAvailable(detail.processingStatus)}
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
